// @flow
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Grid,
  Typography,
  Paper,
  Chip,
  Divider,
  Box,
  Avatar,
  Step,
  Stepper,
  StepLabel,
  StepContent,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Replay as RetryIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Videocam as VideocamIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew,
  OpenInNew as QrCodeIcon,
  HourglassEmpty as PendingIcon,
  Link as LinkIcon,
} from '@material-ui/icons';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignProcess from '../model/SignProcess';
import {QRCodeCanvas} from 'qrcode.react';
import ApiResponse from '../model/ApiResponse';
import { useSnackbar } from 'notistack';
import { AuthContext } from '../context/auth';

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  upload: {
    display: 'none',
  },
  // New modern styling
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  statusChip: {
    marginLeft: 'auto',
    fontWeight: 500,
  },
  successChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '& .MuiChip-icon': {
      color: theme.palette.success.contrastText,
    },
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  },
  videoContainer: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M20 20c0-11.046-8.954-20-20-20v20h20z"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.1,
    },
  },
  videoContent: {
    position: 'relative',
    zIndex: 1,
  },
  videoLogo: {
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      '& .MuiTypography-h6': {
        fontSize: '1.1rem',
        marginLeft: '0 !important',
      },
    },
  },
  validationContainer: {
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  validationContent: {
    position: 'relative',
    zIndex: 1,
  },
  qrContainer: {
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    margin: `${theme.spacing(2)}px auto`,
    display: 'inline-block',
    border: `2px solid ${theme.palette.grey[200]}`,
  },
  securityBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: theme.shape.borderRadius,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    marginTop: theme.spacing(2),
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  optionCard: {
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    border: `2px solid ${theme.palette.grey[200]}`,
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    '&.selected': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
  },
  optionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  optionAvatar: {
    backgroundColor: theme.palette.primary.main,
    width: 56,
    height: 56,
    [theme.breakpoints.down('sm')]: {
      width: 40,
      height: 40,
    },
  },
  optionContent: {
    paddingLeft: theme.spacing(9),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
    },
  },
  stepperContainer: {
    backgroundColor: 'transparent',
    '& .MuiStepLabel-label': {
      fontWeight: 600,
    },
    '& .MuiStepIcon-root': {
      fontSize: '1.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiStepContent-root': {
        paddingLeft: theme.spacing(2),
        borderLeft: `1px solid ${theme.palette.grey[300]}`,
      },
      '& .MuiStepLabel-label': {
        fontSize: '0.9rem',
      },
    },
  },
  termsContainer: {
    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  termsContent: {
    position: 'relative',
    zIndex: 1,
  },
  loadingContainer: {
    textAlign: 'center',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  validatedContainer: {
    background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="20" cy="20" r="4"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.2,
    },
  },
  pendingContainer: {
    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="20" cy="20" r="4"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.2,
    },
  },
  rejectedContainer: {
    background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  validatedContent: {
    position: 'relative',
    zIndex: 1,
  },
}));

type Props = {
  code: string,
  process: SignProcess,
  expanded: boolean,
  disabled: boolean,
  processing: boolean,
  onExpanded: (string) => (event: any, isExpanded: boolean) => void,
  onSave: ({ file: File }) => Promise<any>,
};

const ProcessVideoData = (props: Props) => {
  const { doGet } = useContext(AuthContext);
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { code, process, expanded, onExpanded, disabled, processing } = props;
  const [valid, setValid] = useState<boolean>(false);
  const [termsChecked, setTermsChecked] = useState<boolean>(false);
  const [loading, setLoading] = useState<bool>(false);
  const [selfieUrl, setSelfieUrl] = useState<?string>();
  const [qrSelfieUrl, setQrSelfieUrl] = useState<?string>();
  const [validationUrl, setValidationUrl] = useState<?string>();
  const [qrValidationUrl, setQrValidationUrl] = useState<?string>();

  // Update status logic based on verificationVideoStatus
  let processStatusText = 'Not completed';
  let processStatusIcon = <EditIcon />;
  let statusColor = 'default';

  switch (process.verificationVideoStatus) {
    case 'approved':
      processStatusText = 'Completed';
      processStatusIcon = <DoneIcon />;
      statusColor = 'primary';
      break;
    case 'pending':
      processStatusText = 'Pending';
      processStatusIcon = <PendingIcon />;
      statusColor = 'secondary';
      break;
    case 'rejected':
      processStatusText = 'Rejected';
      processStatusIcon = <RetryIcon />;
      statusColor = 'error';
      break;
    case 'none':
    default:
      processStatusText = 'Not completed';
      processStatusIcon = <EditIcon />;
      statusColor = 'default';
      break;
  }

  if (process.hasDataValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon />;
    statusColor = 'success';
  }

  const loadData = useCallback(() => {
    setLoading(true);
    setTermsChecked(!(process.contractAgreementStatus === 'none' || process.contractAgreementStatus === 'rejected'));
    const path = process.internalValidation ? 'selfie' : 'validation';
    doGet(`/customer/sign-process/${process.code}/${path}-url`)
      .then((response: ApiResponse) => {
        if (response.ok) {
          if (process.internalValidation) {
            setSelfieUrl(String(response.data));
            setQrSelfieUrl(String(response.data));
          } else {
            setValidationUrl(String(response.data));
            setQrValidationUrl(String(response.data));
          }
        } else if (!response.is401Error()) {
          if (response.message === 'El proceso tiene una validación ya completada.') {
            enqueueSnackbar(t('Validation already completed'), {
              variant: 'success',
            });
            // Marcar el proceso como validado
            setValid(true)
          } else {
            const errorMessage = response.message || 'error.unknown';
            enqueueSnackbar(t(errorMessage, errorMessage), {
              variant: 'error',
            });
          }
        }
        setLoading(false);
      });
  }, [doGet, enqueueSnackbar, process, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const haveTerms = !process.internalValidation && !termsChecked && (process.verificationVideoStatus === 'none' || process.verificationVideoStatus === 'rejected');
  const canReload = !loading && !processing && !disabled && !selfieUrl && !validationUrl && (process.internalValidation || termsChecked);

  return (
    <Accordion
      key={code}
      expanded={expanded}
      onChange={onExpanded(code)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon/>}
        aria-controls={`${code}-panel`}
        id={`${code}-panel`}
        className={classes.accordionSummary}
      >
        <Typography className={classes.heading} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <VideocamIcon /> {t(process.internalValidation ? 'Verification video' : 'Validation process')}
        </Typography>
        <Chip
          icon={processStatusIcon}
          label={t(processStatusText)}
          color={statusColor === 'success' ? 'default' : statusColor}
          size="small"
          className={`${classes.statusChip} ${statusColor === 'success' ? classes.successChip : ''}`}
        />
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {(processing || loading) && (
            <Grid item xs={12}>
              <Paper className={classes.loadingContainer} elevation={0}>
                <CircularProgress color="primary" style={{ marginBottom: 16 }} />
                <Typography variant="body2" color="textSecondary">
                  {t('Loading validation data...')}
                </Typography>
              </Paper>
            </Grid>
          )}

          {(process.verificationVideoStatus === 'approved' || valid) && (
            <Grid item xs={12}>
              <div className={classes.validatedContainer}>
                <div className={classes.validatedContent}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    marginBottom: 16,
                    flexDirection: window.innerWidth < 600 ? 'column' : 'row'
                  }}>
                    <DoneAllIcon style={{
                      fontSize: window.innerWidth < 600 ? '2.5rem' : '3rem',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                    }} />
                    <Typography
                      variant="h5"
                      style={{
                        fontWeight: 'bold',
                        fontSize: window.innerWidth < 600 ? '1.25rem' : '1.5rem',
                        textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }}
                    >
                      {t('Validation Completed')}
                    </Typography>
                  </div>

                  <Typography
                    variant="body1"
                    style={{
                      marginBottom: 16,
                      opacity: 0.95,
                      fontSize: window.innerWidth < 600 ? '0.9rem' : '1rem',
                      fontWeight: 500
                    }}
                  >
                    {process.internalValidation ?
                      t('Your selfie verification has been successfully completed') :
                      t('Your identity validation has been successfully completed')
                    }
                  </Typography>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: 20,
                    padding: '8px 16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    margin: '0 auto',
                    maxWidth: 'fit-content'
                  }}>
                    <CheckCircleIcon fontSize="small" />
                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                      {t('Verified and Secure')}
                    </Typography>
                  </div>
                </div>
              </div>
            </Grid>
          )}

          {process.verificationVideoStatus === 'pending' && (
            <Grid item xs={12}>
              <div className={classes.pendingContainer}>
                <div className={classes.validatedContent}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    marginBottom: 16,
                    flexDirection: window.innerWidth < 600 ? 'column' : 'row'
                  }}>
                    <PendingIcon style={{
                      fontSize: window.innerWidth < 600 ? '2.5rem' : '3rem',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                    }} />
                    <Typography
                      variant="h5"
                      style={{
                        fontWeight: 'bold',
                        fontSize: window.innerWidth < 600 ? '1.25rem' : '1.5rem',
                        textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }}
                    >
                      {t('Validation Pending')}
                    </Typography>
                  </div>

                  <Typography
                    variant="body1"
                    style={{
                      marginBottom: 16,
                      opacity: 0.95,
                      fontSize: window.innerWidth < 600 ? '0.9rem' : '1rem',
                      fontWeight: 500
                    }}
                  >
                    {process.internalValidation ?
                      t('Your selfie verification is being processed') :
                      t('Your identity validation is being processed')
                    }
                  </Typography>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: 20,
                    padding: '8px 16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    margin: '0 auto',
                    maxWidth: 'fit-content'
                  }}>
                    <PendingIcon fontSize="small" />
                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                      {t('Processing')}
                    </Typography>
                  </div>
                </div>
              </div>
            </Grid>
          )}

          {process.verificationVideoStatus === 'rejected' && (
            <Grid item xs={12}>
              <Paper
                style={{
                  background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                  color: 'white',
                  borderRadius: 8,
                  padding: 24,
                  marginBottom: 16,
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                elevation={0}
              >
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    marginBottom: 16,
                    flexDirection: window.innerWidth < 600 ? 'column' : 'row'
                  }}>
                    <RetryIcon style={{
                      fontSize: window.innerWidth < 600 ? '2.5rem' : '3rem',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                    }} />
                    <Typography
                      variant="h5"
                      style={{
                        fontWeight: 'bold',
                        fontSize: window.innerWidth < 600 ? '1.25rem' : '1.5rem',
                        textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }}
                    >
                      {t('Validation Rejected')}
                    </Typography>
                  </div>

                  <Typography
                    variant="body1"
                    style={{
                      marginBottom: 16,
                      opacity: 0.95,
                      fontSize: window.innerWidth < 600 ? '0.9rem' : '1rem',
                      fontWeight: 500
                    }}
                  >
                    {process.internalValidation ?
                      t('Your selfie verification was rejected. Please try again.') :
                      t('Your identity validation was rejected. Please try again.')
                    }
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<RetryIcon />}
                    onClick={loadData}
                    disabled={disabled || loading}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      color: 'white',
                      fontWeight: 'bold',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                      }
                    }}
                  >
                    {t('Try Again')}
                  </Button>
                </div>
              </Paper>
            </Grid>
          )}

          {haveTerms && (
            <Grid item xs={12}>
              <div className={classes.termsContainer}>
                <div className={classes.termsContent}>
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: 'bold',
                      marginBottom: 16,
                      fontSize: window.innerWidth < 600 ? '1.1rem' : '1.25rem'
                    }}
                  >
                    <SecurityIcon style={{
                      verticalAlign: 'middle',
                      marginRight: 8,
                      fontSize: window.innerWidth < 600 ? '1.5rem' : '2rem'
                    }} />
                    {t('Terms and Conditions')}
                  </Typography>

                  <Typography
                    variant="body1"
                    style={{
                      marginBottom: 20,
                      opacity: 0.9,
                      fontSize: window.innerWidth < 600 ? '0.9rem' : '1rem'
                    }}
                  >
                    {t('Please accept our terms to proceed with validation')}
                  </Typography>

                  {/* <div className={classes.securityBadge}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={disabled}
                          checked={termsChecked}
                          onChange={(e) => {
                            loadData();
                            setTermsChecked(e.target.checked);
                          }}
                          name='terms'
                          color='primary'
                          style={{ color: 'white' }}
                        />
                      }
                      label={
                        <a
                          href='https://ecuafirma.com/condiciones-generales-ac/'
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{ color: 'white', textDecoration: 'underline' }}
                        >
                          {t('Accept terms and conditions')}
                        </a>
                      }
                    />
                  </div> */}
                </div>
              </div>
            </Grid>
          )}

          {canReload && (
            <Grid item xs={12}>
              <Paper className={classes.sectionPaper} elevation={0}>
                <Button
                  disabled={disabled}
                  variant='contained'
                  color='primary'
                  size="large"
                  startIcon={<RetryIcon />}
                  aria-label='reload'
                  onClick={loadData}
                  style={{ fontWeight: 'bold' }}
                >
                  {t('Load')}
                </Button>
              </Paper>
            </Grid>
          )}

          {(process.internalValidation && !disabled) && (
            <Grid item xs={12}>
              <div className={classes.videoContainer}>
                <div className={classes.videoContent}>
                  <div className={classes.videoLogo}>
                    <VideocamIcon style={{ fontSize: '2.5rem' }} />
                    <Typography
                      variant="h6"
                      component="span"
                      style={{
                        fontWeight: 'bold',
                        marginLeft: window.innerWidth < 600 ? 0 : 8
                      }}
                    >
                      {t('Selfie Verification')}
                    </Typography>
                  </div>

                  <Typography
                    variant="body1"
                    style={{
                      marginBottom: 8,
                      opacity: 0.9,
                      fontSize: window.innerWidth < 600 ? '0.9rem' : '1rem'
                    }}
                  >
                    <strong>{t('Selfie intro')}</strong>
                  </Typography>

                  <Typography
                    variant="body1"
                    style={{
                      marginBottom: 16,
                      opacity: 0.9,
                      fontSize: window.innerWidth < 600 ? '0.9rem' : '1rem'
                    }}
                  >
                    <strong>{t('Image intro')}</strong>
                  </Typography>

                  <div className={classes.securityBadge}>
                    <SecurityIcon fontSize="small" />
                    <Typography variant="caption">
                      {t('Secure identity verification process')}
                    </Typography>
                    <CheckCircleIcon fontSize="small" />
                  </div>
                </div>
              </div>
            </Grid>
          )}

          {(process.internalValidation && !disabled && selfieUrl && termsChecked) && (
            <Grid item xs={12}>
              <Paper className={`${classes.optionCard} selected`} elevation={2}>
                <div className={classes.optionHeader}>
                  <Avatar className={classes.optionAvatar}>
                    <VideocamIcon style={{ fontSize: '1.5rem' }} />
                  </Avatar>
                  <div style={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      style={{
                        fontWeight: 'bold',
                        color: '#333',
                        fontSize: window.innerWidth < 600 ? '1.1rem' : '1.25rem'
                      }}
                    >
                      {t('Selfie Verification Link')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ fontSize: window.innerWidth < 600 ? '0.8rem' : '0.875rem' }}
                    >
                      {t('Use the link or QR code to complete your selfie verification')}
                    </Typography>
                  </div>
                </div>

                <div className={classes.optionContent}>
                  <Stepper orientation="vertical" className={classes.stepperContainer}>
                    <Step active={true}>
                      <StepLabel>
                        <Typography variant="subtitle2">
                          {t('Scan QR code with mobile')}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Box textAlign="center">
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>{t('Browse selfie qr')}</strong>
                          </Typography>
                          <div className={classes.qrContainer}>
                            <QRCodeCanvas
                              value={qrSelfieUrl}
                              size={window.innerWidth < 600 ? 96 : 128}
                              bgColor="#FFFFFF"
                              fgColor="#000000"
                              includeMargin={true}
                              level="L"
                            />
                          </div>
                          <Typography variant="caption" color="textSecondary">
                            {t('Scan with your mobile device')}
                          </Typography>
                        </Box>
                      </StepContent>
                    </Step>

                    <Step active={true}>
                      <StepLabel>
                        <Typography variant="subtitle2">
                          {t('Or access verification link')}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Button
                          size='large'
                          variant='contained'
                          color='primary'
                          startIcon={<LinkIcon />}
                          endIcon={<OpenInNew />}
                          onClick={() => {
                            window.open(selfieUrl, '_blank');
                          }}
                          style={{
                            fontWeight: 'bold',
                            marginBottom: 16,
                            width: window.innerWidth < 600 ? '100%' : 'auto'
                          }}
                        >
                          {t('Browse selfie link')}
                        </Button>
                      </StepContent>
                    </Step>
                  </Stepper>
                </div>
              </Paper>
            </Grid>
          )}

          {(!process.internalValidation && !disabled && validationUrl && termsChecked) && (
            <Grid item xs={12}>
              <Divider style={{ margin: '16px 0' }} />
              <div className={classes.validationContainer}>
                <div className={classes.validationContent}>
                  <Typography
                    variant="h5"
                    style={{
                      fontWeight: 'bold',
                      marginBottom: 16,
                      fontSize: window.innerWidth < 600 ? '1.25rem' : '1.5rem'
                    }}
                  >
                    <QrCodeIcon style={{
                      verticalAlign: 'middle',
                      marginRight: 8,
                      fontSize: window.innerWidth < 600 ? '1.5rem' : '2rem'
                    }} />
                    {t('Validation Ready')}
                  </Typography>

                  <Typography
                    variant="body1"
                    style={{
                      marginBottom: 20,
                      opacity: 0.9,
                      fontSize: window.innerWidth < 600 ? '0.9rem' : '1rem'
                    }}
                  >
                    {t('Use the link or scan the QR code to complete validation')}
                  </Typography>

                  <div className={classes.qrContainer}>
                    <QRCodeCanvas
                      value={qrValidationUrl}
                      size={window.innerWidth < 600 ? 96 : 128}
                      bgColor="#FFFFFF"
                      fgColor="#000000"
                      includeMargin={true}
                      level="L"
                    />
                  </div>

                  <Typography variant="caption" style={{ opacity: 0.8, display: 'block', marginTop: 8 }}>
                    {t('Browse validation qr')}
                  </Typography>

                  <Button
                    size='large'
                    variant='contained'
                    style={{
                      display: 'block',
                      backgroundColor: 'white',
                      color: '#11998e',
                      fontWeight: 'bold',
                      margin: '10px auto',
                      width: window.innerWidth < 600 ? '100%' : 'auto'
                    }}
                    startIcon={<OpenInNew />}
                    onClick={() => {
                      window.open(validationUrl, '_blank');
                    }}>
                    {t('Browse validation link')}
                  </Button>

                </div>
              </div>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default ProcessVideoData;
